import { FocusMonitor } from '@angular/cdk/a11y';
import {
  AfterViewInit, ChangeDetectorRef, Component, ElementRef, EventEmitter, Input, OnDestroy, OnInit, Output, ViewChild
} from '@angular/core';
import { NgModel } from '@angular/forms';
import { filter } from 'rxjs/operators';

/**
 * This component takes an optional time value as input to initialize
 * the time picker with, and it outputs a new time value every time
 * an update is made to the timepicker.
 *
 * The time values that this component expects and returns
 * are 12-hour time strings with the following format:
 * "{hour}:{min} {am|pm}", where `hour` is a zero padded number string
 * from 01 to 12, and `min` is a zero padded number string from 00 to 59.
 */
@Component({
  selector: 'time-picker',
  templateUrl: './time-picker.component.html',
  styleUrls: ['./time-picker.component.scss'],
  standalone: false
})
export class TimePickerComponent implements OnInit, AfterViewInit, OnDestroy {
  /** The inputted time value to initialize the time picker with. */
  @Input() time: string;

  /** The outputted time value that is emitted whenever a change occurs. */
  @Output() timeChange = new EventEmitter<string>();

  /** Emits whenever the timepicker is no longer being focused. */
  @Output() onBlur = new EventEmitter<void>();

  @ViewChild('hourInput') hourInput: ElementRef;
  @ViewChild('minuteInput') minuteInput: ElementRef;
  @ViewChild('timePickerContainer') container: ElementRef;

  @ViewChild('hourModel') hourModel: NgModel;
  @ViewChild('minuteModel') minuteModel: NgModel;

  hourVal: number;
  minuteVal: number;
  meridianVal: 'am' | 'pm';

  ngOnInit(): void {
    this.initializeTimeValues();
  }

  ngAfterViewInit(): void {
    this.focusMonitorService.focusVia(this.hourInput, 'program');

    this.focusMonitorService.monitor(this.container, true).pipe(
      filter((focusOrigin) => !focusOrigin)
    ).subscribe(
      (_) => this.onBlur.emit()
    );
  }

  ngOnDestroy(): void {
    this.focusMonitorService.stopMonitoring(this.container);
  }

  constructor(
    private focusMonitorService: FocusMonitor,
    public changeDetectionRef: ChangeDetectorRef
  ) { }

  handleHourUpdate(): void {
    const newHour = this.hourInput.nativeElement.value != null
      && this.hourInput.nativeElement.value !== ''
      ? +this.hourInput.nativeElement.value
      : null;

    if (Number.isInteger(newHour) && newHour >= 1 && newHour <= 12) {
      this.hourVal = newHour;
      this.timeChange.emit(this.generateTimeOutputString());
    } else if (newHour == null || newHour === 0) {
      /* since 0 isn't a valid hour we consider it to be null and clear out the input text.
      We do this instead of preventing the value change like we do with other invalid values because
      If we don't handle 0 as null here then the user can never delete their single digit number. */
      this.hourVal = null;
    }

    this.hourModel.valueAccessor.writeValue(this.zeroPadValue(this.hourVal));
  }

  handleMinUpdate(): void {
    const newMin = this.minuteInput.nativeElement.value != null
      && this.minuteInput.nativeElement.value !== ''
      ? +this.minuteInput.nativeElement.value
      : null;

    if (Number.isInteger(newMin) && newMin >= 0 && newMin <= 59) {
      this.minuteVal = newMin;
      this.timeChange.emit(this.generateTimeOutputString());
    } else if (newMin == null) {
      this.minuteVal = null;
    }

    this.minuteModel.valueAccessor.writeValue(this.zeroPadValue(this.minuteVal));
  }

  handleMeridianUpdate(newMeridianInput: any): void {
    const newMeridian = (newMeridianInput as string)?.toUpperCase?.();

    if (newMeridian === 'AM' || newMeridian === 'PM') {
      this.meridianVal = newMeridianInput;
      this.timeChange.emit(this.generateTimeOutputString());
    }
  }

  /**
   * Parses the inputted time string and initializes the
   * time component properties with their respective values.
   */
  private initializeTimeValues(): void {
    if (!this.time) { return }

    const timeTokens = this.time.split(':');
    const [hourStr, minStr, meridianStr] = [timeTokens[0], ...timeTokens[1].split(' ')];

    this.hourVal = +hourStr;
    this.minuteVal = +minStr;
    this.meridianVal = meridianStr as 'am' | 'pm';
  }

  zeroPadValue(numberToZeroPad: number): string {
    if (numberToZeroPad == null) { return null }

    return `${numberToZeroPad >= 0 && numberToZeroPad <= 9 ? '0' : ''}${numberToZeroPad}`;
  }

  /**
   * Generates and returns the formatted time output string
   * based on the current hour, minute, and meridian inputs.
   */
  private generateTimeOutputString(): string {
    if (this.hourVal == null || this.minuteVal == null || this.meridianVal == null) {
      return '';
    }

    return `${this.zeroPadValue(this.hourVal)}:${this.zeroPadValue(this.minuteVal)} ${this.meridianVal}`;
  }
}
